import type {CopilotSpace, CopilotSpaceId} from '@github-ui/copilot-chat-types'
import {customCopilotApiPath} from '@github-ui/copilot-chat-helpers/custom-copilots-helpers'
import {COPILOT_SPACES_QUERY_KEY, copilotSpacesQueryKey} from '@github-ui/copilot-shared-ui/spaces/fetch'
import {useMutation, useQueryClient} from '@github-ui/react-query'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import {useEffect, useState} from 'react'

import type {CopilotSpaceResource} from '../types'
import {resourceMetadataToRailsPayload} from '../utils/payload-mapper'

/**
 * The input type for the deleteCopilotSpaceResources mutation.
 */
export type Input = CopilotSpaceResource[]

/**
 * Deletes resources from a Copilot Space without requiring a CopilotChatManagerProvider.
 * Use this on surfaces (like the Spaces admin pages) that render outside the chat context.
 */
export const useDeleteCopilotSpaceResources = (id?: CopilotSpaceId) => {
  const queryClient = useQueryClient()
  const [shouldResyncCopilotSpace, setShouldResyncCopilotSpace] = useState(false)

  const {
    mutateAsync: deleteCopilotSpaceResources,
    isPending,
    ...rest
  } = useMutation({
    mutationFn: async (input: Input) => {
      if (!id) return
      setShouldResyncCopilotSpace(true)

      const url = customCopilotApiPath(id)
      const method = 'PUT'

      const body = {
        custom_copilot: {
          resources_attributes: input.map(r => ({
            id: r.databaseId,
            copilot_chat_attachment_id: r.copilotChatAttachmentId,
            resource_type: r.type,
            // The _destroy param is a special param used by Rails' accepts_nested_attributes_for. If set to true
            // the record will be deleted.
            _destroy: true,
            metadata: resourceMetadataToRailsPayload(r),
          })),
        },
      }

      const response = await verifiedFetchJSON(url, {
        method,
        body,
      })

      const data = await response.json()

      // If status is 404, it is either because:
      // 1. The resource might have been deleted already.
      // 2. The user don't have access to the resource.
      // In either case, we don't want to revert the optimistic update.
      if (!response.ok && response.status !== 404) {
        throw new Error('Unable to delete resources.')
      }

      return data as CopilotSpace
    },
  })

  useEffect(() => {
    if (id && shouldResyncCopilotSpace && !isPending) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setShouldResyncCopilotSpace(false)
      void queryClient.invalidateQueries({queryKey: COPILOT_SPACES_QUERY_KEY})
      void queryClient.invalidateQueries({queryKey: copilotSpacesQueryKey(id)})
    }
  }, [shouldResyncCopilotSpace, isPending, id, queryClient])

  return {
    deleteCopilotSpaceResources,
    isPending,
    ...rest,
  }
}
